import { DateRange } from "react-day-picker";
import { createContext, useContext, useMemo } from "react";
import { subDays } from "date-fns";
import { processDateRange, ProcessedDateRange } from "@/lib/date-utils";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";

interface DashboardDateRangeContextType {
  dateRange: DateRange | undefined;
  setDateRange: (dateRange: DateRange | undefined) => void;
  processedDateRange: ProcessedDateRange;
}

const DashboardDateRangeContext = createContext<
  DashboardDateRangeContextType | undefined
>(undefined);
const DASHBOARD_DATE_RANGE_STORAGE_KEY =
  "pizza-admin:dashboard-date-range:v1";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const parseStoredDate = (value: unknown) => {
  if (typeof value !== "string") {
    return undefined;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const deserializeDateRange = (
  storedValue: unknown,
  fallback: DateRange | undefined,
): DateRange | undefined => {
  if (!isRecord(storedValue)) {
    return fallback;
  }

  const from = parseStoredDate(storedValue.from);
  const to = parseStoredDate(storedValue.to);

  return from || to ? { from, to } : fallback;
};

const serializeDateRange = (dateRange: DateRange | undefined) =>
  dateRange
    ? {
        from: dateRange.from?.toISOString(),
        to: dateRange.to?.toISOString(),
      }
    : undefined;

export const DashboardDateRangeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [dateRange, setDateRange] = useLocalStorageState<
    DateRange | undefined
  >(DASHBOARD_DATE_RANGE_STORAGE_KEY, undefined, {
    deserialize: deserializeDateRange,
    serialize: serializeDateRange,
  });

  const processedDateRange = useMemo(() => {
    // If no date range is set, use default of past 7 days
    if (!dateRange) {
      return processDateRange({
        from: subDays(new Date(), 7),
        to: new Date(),
      });
    }
    return processDateRange(dateRange);
  }, [dateRange]);

  return (
    <DashboardDateRangeContext.Provider
      value={{ dateRange, setDateRange, processedDateRange }}
    >
      {children}
    </DashboardDateRangeContext.Provider>
  );
};

export const useDashboardDateRange = () => {
  const context = useContext(DashboardDateRangeContext);
  if (context === undefined) {
    throw new Error(
      "useDashboardDateRange must be used within a DashboardDateRangeProvider",
    );
  }
  return context;
};
